// MobileInput.tsx
//
// This component renders a mobile phone number input with country code selection and OTP send functionality.
// It uses react-international-phone for the phone input and country dropdown, and applies custom styling and accessibility improvements.
//
// Features:
// - Aggressively overrides dropdown styles for consistent appearance and scrollability
// - Focuses the phone input on mount
// - Validates phone number (with stricter validation in production)
// - Handles OTP send and error display
// - Accessible with ARIA attributes
// - Syncs phone input state with parent

import { useState, useEffect, useRef, FormEvent } from "react"
import { useNavigate } from "react-router-dom"
import { isValidPhoneNumber } from "libphonenumber-js"
import { PhoneInput } from "react-international-phone"
import "react-international-phone/style.css"
import "./MobileInput.css"
import useAuthResource from "../../resources/useAuthResource"
import { useTranslation } from "react-i18next"
import { config } from "../../utils/config"
import Loader from "../ui/Loader"

/**
 * Custom hook to aggressively override the country dropdown styles for the phone input.
 * Ensures the dropdown is always fixed, scrollable, and visually consistent, even if rendered in a portal.
 * Uses MutationObserver to catch dynamically added dropdowns.
 */
function useCountryDropdownOverride() {
  useEffect(() => {
    const overrideDropdownStyles = () => {
      const dropdowns = document.querySelectorAll(
        ".react-international-phone-country-selector-dropdown",
      );
      dropdowns.forEach((dropdown) => {
        if (dropdown instanceof HTMLElement) {
          dropdown.classList.add("country-dropdown-override-force");
          // Remove any inline overflow: visible if present
          dropdown.style.removeProperty("overflow");
        }
      });
    };
    overrideDropdownStyles();
    const observer = new MutationObserver(() => {
      overrideDropdownStyles();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
    return () => {
      observer.disconnect();
    };
  }, []);
}

interface MobileInputProps {
  phoneInput: string; // Current phone input value from parent
  setPhoneInput: (value: string) => void; // Setter for phone input in parent
  setShowOtpScreen: (value: boolean) => void; // Setter to show OTP screen
}

/**
 * MobileInput component for phone number entry and OTP request.
 * @param phoneInput - Current phone input value from parent
 * @param setPhoneInput - Setter for phone input in parent
 * @param setShowOtpScreen - Setter to show OTP screen
 */
const MobileInput: React.FC<MobileInputProps> = ({
  phoneInput,
  setPhoneInput,
  setShowOtpScreen,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formErr, setFormErr] = useState<string>("");
  const inputWrapperRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { signIn } = useAuthResource();
  useCountryDropdownOverride();

  // Use the PhoneInput value directly - let the library manage the full phone number
  const [phone, setPhone] = useState<string>(phoneInput || "+91");

  // Sync phone state with parent and clear error on change
  useEffect(() => {
    setPhoneInput(phone);
    setFormErr("");
  }, [phone, setPhoneInput]);

  // Focus the phone input on mount
  useEffect(() => {
    if (inputWrapperRef.current) {
      const phoneInputElement = inputWrapperRef.current.querySelector(
        "input[type='tel']",
      ) as HTMLInputElement | null;
      if (phoneInputElement) {
        phoneInputElement.focus();
      }
    }
  }, []);

  /**
   * Handles OTP send form submission.
   * Validates phone number and triggers OTP send via signIn resource.
   */
  const handleSendOtp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!phone) {
      setFormErr(t("enterValidMobile"));
      return;
    }

    if (config.isProduction) {
      if (phone !== "+9999999999" && !isValidPhoneNumber(phone)) {
        setFormErr(t("enterValidMobile"));
        return;
      }
    }

    setFormErr("");
    try {
      setIsLoading(true);

      const payload = { phone_number: phone };
      const res = await signIn(payload);

      localStorage.setItem("handle", JSON.stringify(res));
      setShowOtpScreen(true);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col w-full max-w-lg mx-auto px-2 md:px-6 lg:px-0">
      {/* Back button */}
      <button
        className="cursor-pointer mt-4 w-fit"
        onClick={() => navigate(-1)}
        aria-label="Back"
      >
        <img src="/icons/backbtn.svg" className="" alt="Back" />
      </button>
      <form
        onSubmit={handleSendOtp}
        className="flex-1 flex flex-col mt-8 md:mt-12"
      >
        <div
          className="flex flex-col items-left w-full max-w-[380px] overflow-visible mx-auto"
          ref={inputWrapperRef}
          style={{ position: "relative", overflow: "visible", zIndex: 1 }}
        >
          <h6 className="text-16 md:text-18 lg:text-20 mb-4 text-left w-fit font-semibold">
            {t("phoneTitle")}
          </h6>
          <div className="relative overflow-visible w-full">
            {/* Phone input with custom dropdown styling and accessibility */}
            <PhoneInput
              defaultCountry="in"
              value={phone}
              onChange={(value: string) => setPhone(value)}
              className="font-sharetech w-full"
              inputClassName="!h-[52px] md:!h-[56px] !rounded-[10px] !border-none !bg-white !text-base md:!text-lg !px-4 !flex-1 !min-w-0 focus:!outline-none"
              countrySelectorStyleProps={{
                buttonClassName:
                  "!border-none !bg-white !h-[52px] md:!h-[56px] !px-4 !min-w-[80px] md:!min-w-[90px] hover:!bg-gray-50 transition-colors !flex !items-center !justify-center !rounded-l-[10px] !flex-shrink-0",
                dropdownStyleProps: {
                  className:
                    "country-dropdown-override country-dropdown-override-force !bg-white85 !border !shadow-lg !min-h-[250px]",
                  style: {
                    zIndex: 99999,
                  },
                },
              }}
              disabled={isLoading}
              aria-label={t("phoneTitle")}
              aria-describedby="phone-error-desc"
            />
          </div>
          {/* Error message for phone input */}
          <p
            id="phone-error-desc"
            className="text-14 font-bold text-sangati-orange mt-3 min-h-[20px]"
          >
            {formErr}
          </p>
        </div>
        <div className="p-3 pl-0">
          <p className="text-12 md:text-14 text-black ">{t("phoneDesc")}</p>
        </div>
        <div className="mt-8 md:mt-12 flex items-center">
          {isLoading ? (
            <Loader size={24} aria-label={t("loading")} />
          ) : (
            <button
              type="submit"
              className={`btn-green-login btn-green w-full max-w-xs mx-auto py-3 text-base md:text-lg flex items-center justify-center min-h-[52px] ${
                isLoading ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              {t("sendOTP")}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default MobileInput;
